import React, { useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { CreatePlantInput } from 'views/feed-management/features/plants/types/create-plant-input.type';
import { Plant } from 'views/feed-management/features/plants/types/plant.type';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import { Box, Button, Typography, useTheme } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import Dialog from 'components/dialog';
import Error from 'components/error';
import FilterSidebar from 'components/filter-sidebar';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';

import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import { getFeedManagementMarkets } from 'views/feed-management/features/plants/api/get-markets';
import { putFeedManagementPlant } from 'views/feed-management/features/plants/api/update-plants';
import FeedManagementTabsHeader from '../../../components/feed-management-tabs-header';
import { getFeedManagementPlant } from '../api/get-plants';
import Paper from 'components/paper';

type ParamTypes = {
  code: 'string';
};

type Props = {};

const EditPlant: React.FC<Props> = () => {
  const params = useParams<ParamTypes>();
  const intl = useIntl();
  const snackbarDispatch = useSnackbarDispatch();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const removeDialogRef = useRef<React.ElementRef<typeof Dialog>[]>([]);

  const PAGE_NAME_ID = 'feedManagement.editPlant';

  // STATE
  const [selectMarket, setSelectMarket] = useState<number>(0);

  // FETCH DATA
  const {
    data: plant,
    error,
    isLoading,
    isError,
  } = useGet(['getFeedManagementPlant', params.code], (context) => getFeedManagementPlant(String(context.queryKey[1])), {
    enabled: !!params.code,
  });
  const { data: markets } = useGet(['getFeedManagementMarkets'], () => getFeedManagementMarkets());

  // POST DATA
  const updatePlant = usePost<Plant, CreatePlantInput>(
    (request) => putFeedManagementPlant(request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      queryClient.invalidateQueries({
        queryKey: ['getFeedManagementPlant']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // HANDLERS
  const handleUpdateMarket = (request: CreatePlantInput) => {
    updatePlant.mutate(request);
  };

  const filteredMarkets = useMemo(() => {
    return markets?.filter((market) => !plant?.markets.map((plantMarket) => plantMarket.id).includes(market.id));
  }, [markets, plant]);
  // RENDERS

  return isError ? (
    <Error error={error} />
  ) : !plant || isLoading ? (
    <LoadingSpinner />
  ) : (
    <>
      <FeedManagementTabsHeader
        utils={
          <Breadcrumbs
            crumbs={[
              {
                text: plant.name,
                url: plant.code,
              },
            ]}
          />
        }
      />
      <Paper transparent>
        {filteredMarkets?.length ? (
          <FilterSidebar
            clearFilters={() => {
              setSelectMarket(0);
            }}>
            <Select
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.selectMarketLabel` })}
              value={selectMarket}
              onChange={(event) => setSelectMarket(Number(event.target.value))}>
              {filteredMarkets?.map((market) => (
                <RadioSelectItem key={market.id} value={market.id} selected={market.id === selectMarket}>
                  {market.name}
                </RadioSelectItem>
              ))}
            </Select>
            <Button
              onClick={() =>
                handleUpdateMarket({
                  code: plant!.code,
                  name: plant!.name,
                  markets: [...plant!.markets.map((m) => m.id), selectMarket],
                })
              }>
              <FormattedMessage id="general.add" values={{ type: '' }} />
            </Button>
          </FilterSidebar>
        ) : null}

        <Typography variant="h3" color={theme.palette.common.white}>
          <FormattedMessage id={`${PAGE_NAME_ID}.list.title`} />
        </Typography>
        <List
          sx={{ mt: 3 }}
          options={[
            { id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.nameLabel` }), fullWidth: true },
            { id: '', label: '' },
          ]}
          orderBy="name"
          isLoading={isLoading}
          noResult={{
            show: plant.markets.length <= 0,
            text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
          }}>
          {plant.markets.map((market, idx) => (
            <ListRow key={market.id} sx={{ backgroundColor: `theme.palette.lightBlue.o25 !important` }}>
              <ListCell align="left">{market.name}</ListCell>
              <ListCell align="center">
                <Dialog
                  maxWidth="xs"
                  ref={(el) => {
                    if (el) return (removeDialogRef.current[idx] = el);
                  }}
                  title={`Delete (${market.name})`}
                  button={
                    <Button size="small">
                      <FormattedMessage id="general.delete" />
                    </Button>
                  }>
                  <Box display={'flex'} flexDirection={'column'} gap={4}>
                    <Box>
                      <FormattedMessage id="general.deleteComment" />
                    </Box>
                    <Box display={'flex'} justifyContent={'end'} gap={4}>
                      <Button variant="outlined" type="submit" size="small" onClick={() => removeDialogRef.current[idx]?.close()}>
                        <FormattedMessage id="general.cancel" />
                      </Button>
                      <Button
                        type="submit"
                        onClick={() =>
                          handleUpdateMarket({
                            code: plant.code,
                            name: plant.name,
                            markets: plant.markets.filter((m) => m.id !== market.id).map((m) => m.id),
                          })
                        }>
                        <FormattedMessage id="general.delete" />
                      </Button>
                    </Box>
                  </Box>
                </Dialog>
              </ListCell>
            </ListRow>
          ))}
        </List>
      </Paper>
    </>
  );
};

export default EditPlant;
